import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DeepPartial} from 'util/Types';

import {PIPELINE_STEP_SHAPES} from './GeneratedPipelineStepShapes';
import {PipelineStepShape, StepDataShape} from './StepShapeUtils';

export const getLatestStepVersion = (stepType: PipelineStepType): number =>
  PIPELINE_STEP_SHAPES[stepType].length - 1;

const getStepShape = (stepType: PipelineStepType): PipelineStepShape =>
  PIPELINE_STEP_SHAPES[stepType][getLatestStepVersion(stepType)];

const ALL_STEP_TYPES = Object.values(PipelineStepType).filter(
  (stepType) => !getStepShape(stepType).isDeprecated
) as Array<PipelineStepType>;

export const getOutputStepDatas = (stepType: PipelineStepType): Array<StepDataShape> =>
  Object.values(getStepShape(stepType).outputDataShapes);

export const getInputStepDatas = (stepType: PipelineStepType): Array<StepDataShape> =>
  Object.values(getStepShape(stepType).inputDataShapes);

const nullOrEqual = <T>(a: T | null, b: T): boolean => a == null || a === b;

function stepDataShapeMatchesFilter(
  filter: Partial<StepDataShape> | undefined,
  actual: StepDataShape
): boolean {
  return (
    nullOrEqual(filter?.systemName, actual.systemName) &&
    nullOrEqual(filter?.displayName, actual.displayName) &&
    nullOrEqual(filter?.isMulti, actual.isMulti) &&
    nullOrEqual(filter?.kind, actual.kind) &&
    nullOrEqual(filter?.isRequired, actual.isRequired)
  );
}

interface StepTypeConnection {
  fromStepType: PipelineStepType;
  fromStepData: StepDataShape;
  toStepType: PipelineStepType;
  toStepData: StepDataShape;
}

/**
 * Search for all step types that can be connected to a given step type, or step data.
 *
 * Each property specified in `filter: StepTypeConnection` narrows the search to only those.
 *
 * To find step types that can be inserted following a given `fromStepType`:
 *
 *    getPossibleEdges({fromStepType}).map(({toStepType}) => toStepType);
 *
 * To narrow the search to only include single-input step datas:
 *
 *    getPossibleEdges({fromStepType, toStepData: {isMulti: false}});
 *
 * To find the step datas which connect two step types:
 *
 *    const {fromStepData, toStepData} = getPossibleEdges({fromStepType, toStepType})[0];
 *
 * To check if a to step type can be connected to a from step data:
 *
 *    getPossibleEdges({fromStepType, fromStepData, toStepType, toStepData}).length > 0;
 *
 * This function assumes:
 * • All step types are their latest versions
 * • `isMulti` and non-`isMulti` step datas can be connected
 */
export function getPossibleEdges(filter: DeepPartial<StepTypeConnection>): Array<StepTypeConnection> {
  const fromStepTypes = filter.fromStepType ? [filter.fromStepType] : ALL_STEP_TYPES;
  const toStepTypes = filter.toStepType ? [filter.toStepType] : ALL_STEP_TYPES;

  return fromStepTypes.flatMap((fromStepType) =>
    getOutputStepDatas(fromStepType)
      .filter((fromStepData) => stepDataShapeMatchesFilter(filter.fromStepData, fromStepData))
      .flatMap((fromStepData) =>
        toStepTypes.flatMap((toStepType) =>
          getInputStepDatas(toStepType)
            .filter((toStepData) => stepDataShapeMatchesFilter(filter.toStepData, toStepData))
            .filter((toStepData) => toStepData.kind === fromStepData.kind)
            .flatMap((toStepData) => ({fromStepType, fromStepData, toStepType, toStepData}))
        )
      )
  );
}

export function getSourceStepTypes(): Array<PipelineStepType> {
  return ALL_STEP_TYPES.filter((stepType) => {
    const inputs = getInputStepDatas(stepType);
    return inputs.length === 0 || inputs.every((input) => !input.isRequired);
  });
}
